import React from "react";
import { StatusKey } from "@/components/knowledge/StatusKey";
import { Scale, CheckCircle2, AlertTriangle, Compass } from "lucide-react";
export function HowWeLabelClaims() {
  return <section id="epistemic-standards" className="w-full scroll-mt-24 min-w-0">
      {/* Header */}
      <div className="mb-8">
        <span className="ql-eyebrow mb-2">EPISTEMIC STANDARDS</span>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
          How We Label Claims
        </h2>
        <p className="font-sans text-sm sm:text-base text-muted-foreground mt-1 max-w-2xl">
          Every assertion in the Architecture of Knowledge carries an explicit epistemic status. We reject intellectual dogmatism without falling into cynical relativism.
        </p>
      </div>

      {/* Four Core Principles Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 min-w-0">
        <div className="ql-glass p-5 rounded-xl border border-border flex flex-col justify-between min-w-0">
          <div>
            <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-3">
              <Compass className="w-4 h-4" />
            </div>
            <h3 className="font-serif text-sm sm:text-base font-bold text-foreground mb-1.5">
              Investigate, Not Direct
            </h3>
            <p className="font-sans text-xs text-muted-foreground leading-relaxed">
              We teach how to investigate, not what to think. Our goal is cognitive autonomy through rigorous methods rather than doctrine.
            </p>
          </div>
        </div>

        <div className="ql-glass p-5 rounded-xl border border-border flex flex-col justify-between min-w-0">
          <div>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-3">
              <Scale className="w-4 h-4" />
            </div>
            <h3 className="font-serif text-sm sm:text-base font-bold text-foreground mb-1.5">
              Neutrality Requires Scrutiny
            </h3>
            <p className="font-sans text-xs text-muted-foreground leading-relaxed">
              Neutrality is not refusing to evaluate evidence. True neutrality means applying the same unyielding evidentiary standard to every claim.
            </p>
          </div>
        </div>

        <div className="ql-glass p-5 rounded-xl border border-border flex flex-col justify-between min-w-0">
          <div>
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-3">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <h3 className="font-serif text-sm sm:text-base font-bold text-foreground mb-1.5">
              Confidence Is Not Certainty
            </h3>
            <p className="font-sans text-xs text-muted-foreground leading-relaxed">
              High subjective confidence is never equivalent to logical or empirical certainty. Fallibilism requires being open to revision.
            </p>
          </div>
        </div>

        <div className="ql-glass p-5 rounded-xl border border-border flex flex-col justify-between min-w-0">
          <div>
            <div className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400 mb-3">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <h3 className="font-serif text-sm sm:text-base font-bold text-foreground mb-1.5">
              Possible Is Not Probable
            </h3>
            <p className="font-sans text-xs text-muted-foreground leading-relaxed">
              Merely imagining a scenario does not assign it a high probability. Distinguishing logical possibility from empirical likelihood is paramount.
            </p>
          </div>
        </div>
      </div>

      {/* The 9-Tier Epistemic Status Key */}
      <StatusKey />
    </section>;
}